import { useMemo } from 'react'
import { Layer, Map as MapboxMap, Source } from 'react-map-gl/mapbox'
import type { FilterSpecification } from 'mapbox-gl'
import { useMantineColorScheme, useMantineTheme } from '@mantine/core'
import type { FeatureCollection, Polygon as GeoJsonPolygon } from 'geojson'
import 'mapbox-gl/dist/mapbox-gl.css'
import type { RouteFragmentStatistics, RouteSegment, RouteStatisticsStreetSegment } from '@/api/main/types.gen'
import { MAPBOX_PUBLIC_TOKEN } from '@/constants'
import { determineMapCenter } from '@/components/Navigation/utils/mapUtils'
import AreaPolygons from '@/components/RoutePlanner/AreaPolygons'
import {
  PLANNER_PARKING_FILL_COLOR,
  PLANNER_PARKING_FILL_OPACITY,
  PLANNER_PARKING_LINE_COLOR,
  PLANNER_PARKING_LINE_WIDTH,
} from '@/components/RoutePlanner/constants'
import {
  firstSegmentCenter,
  fragmentEndpointsToFeatureCollection,
  routePlanToFeatureCollection,
  type RoutePlanLineVisual,
} from '@/components/routeStatisticsMapData'

const NOT_REQUIRED_DASH: [number, number] = [10 / 3, 7 / 3]
const SINGLE_PASS_DASH: [number, number] = [5 / 3, 2 / 3]

const visualFilter = (visual: RoutePlanLineVisual): FilterSpecification => ['==', ['get', 'visual'], visual]

type RouteStatisticsMapProps = {
  streetSegments: Record<string, RouteStatisticsStreetSegment>
  routePlan: Array<RouteSegment>
  fragments: Array<RouteFragmentStatistics>
  selectedFragment: number
  highlightedSegment: number
  parkingPolygons: FeatureCollection<GeoJsonPolygon> | null
}

const RouteStatisticsMap = ({
  streetSegments,
  routePlan,
  fragments,
  selectedFragment,
  highlightedSegment,
  parkingPolygons,
}: RouteStatisticsMapProps) => {
  const theme = useMantineTheme()
  const { colorScheme } = useMantineColorScheme()
  const fragment = fragments[selectedFragment]
  const lines = useMemo(
    () =>
      routePlanToFeatureCollection({
        streetSegments,
        routePlan,
        fragment,
        highlightedSegment,
      }),
    [streetSegments, routePlan, fragment, highlightedSegment]
  )
  const endpoints = useMemo(() => fragmentEndpointsToFeatureCollection(fragment), [fragment])
  const initialViewState = useMemo(() => {
    const center = firstSegmentCenter(streetSegments) ?? determineMapCenter()
    return { zoom: 14, longitude: center.longitude, latitude: center.latitude }
  }, [streetSegments])
  const mapStyle = `mapbox://styles/mapbox/${colorScheme === 'light' || colorScheme === 'auto' ? 'streets-v12' : 'dark-v11'}`

  const lineLayers: Array<{
    id: string
    visual: RoutePlanLineVisual
    color: string
    width: number
    dash?: [number, number]
  }> = [
    { id: 'route-statistics-solid-active', visual: 'solidActive', color: theme.colors.blue[4], width: 3 },
    { id: 'route-statistics-solid-inactive', visual: 'solidInactive', color: theme.colors.gray[7], width: 3 },
    {
      id: 'route-statistics-not-required',
      visual: 'notRequired',
      color: theme.colors.blue[2],
      width: 3,
      dash: NOT_REQUIRED_DASH,
    },
    {
      id: 'route-statistics-single-active',
      visual: 'singleActive',
      color: theme.colors.blue[4],
      width: 3,
      dash: SINGLE_PASS_DASH,
    },
    {
      id: 'route-statistics-single-inactive',
      visual: 'singleInactive',
      color: theme.colors.gray[7],
      width: 3,
      dash: SINGLE_PASS_DASH,
    },
    { id: 'route-statistics-highlight-current', visual: 'highlightCurrent', color: theme.colors.violet[5], width: 5 },
    { id: 'route-statistics-highlight-next', visual: 'highlightNext', color: theme.colors.red[6], width: 5 },
    { id: 'route-statistics-highlight-next2', visual: 'highlightNext2', color: theme.colors.yellow[6], width: 5 },
  ]

  return (
    <MapboxMap
      mapboxAccessToken={MAPBOX_PUBLIC_TOKEN}
      mapStyle={mapStyle}
      initialViewState={initialViewState}
      style={{ width: '100%', height: '100%' }}
      onLoad={event => event.target.resize()}
    >
      {parkingPolygons ? (
        <AreaPolygons
          data={parkingPolygons}
          options={{
            sourceId: 'route-statistics-parking',
            fillLayerId: 'route-statistics-parking-fill',
            lineLayerId: 'route-statistics-parking-line',
            fillColor: PLANNER_PARKING_FILL_COLOR,
            fillOpacity: PLANNER_PARKING_FILL_OPACITY,
            fillHoverOpacity: PLANNER_PARKING_FILL_OPACITY,
            lineColor: PLANNER_PARKING_LINE_COLOR,
            lineWidth: PLANNER_PARKING_LINE_WIDTH,
            lineHoverColor: PLANNER_PARKING_LINE_COLOR,
          }}
        />
      ) : null}
      <Source id="route-statistics-plan" type="geojson" data={lines}>
        {lineLayers.map(layer => (
          <Layer
            key={layer.id}
            id={layer.id}
            type="line"
            filter={visualFilter(layer.visual)}
            paint={{
              'line-color': layer.color,
              'line-width': layer.width,
              ...(layer.dash ? { 'line-dasharray': layer.dash } : {}),
            }}
          />
        ))}
      </Source>
      <Source id="route-statistics-endpoints" type="geojson" data={endpoints}>
        <Layer
          id="route-statistics-start"
          type="circle"
          filter={['==', ['get', 'role'], 'start']}
          paint={{ 'circle-radius': 10, 'circle-color': theme.colors.green[6] }}
        />
        <Layer
          id="route-statistics-end"
          type="circle"
          filter={['==', ['get', 'role'], 'end']}
          paint={{ 'circle-radius': 10, 'circle-color': theme.colors.red[6] }}
        />
      </Source>
    </MapboxMap>
  )
}

export default RouteStatisticsMap
